/**
Author - Sahil Dhingra
Git - https://github.com/sahildhingra-221
**/

import React, {useState} from "react";
import PropTypes from "prop-types";
import ManageBlogFaqs from "./ManageBlogFaqs";
import {WealthyEnv, triggerToast} from "helpers";
import styled from "styled-components";
import {updateFaq} from "api";

const resolveExternalId = (faq) => {
  if (!faq || typeof faq !== "object") return null;
  const nested = faq.faq && typeof faq.faq === "object" ? faq.faq : null;
  const raw =
    faq.externalId ??
    faq.external_id ??
    faq.faq_id ??
    (nested && (nested.externalId ?? nested.external_id));
  if (raw == null || raw === "") return null;
  return String(raw);
};

const normalizeFaqForEditor = (faq) => {
  if (!faq || typeof faq !== "object") return faq;
  const externalId = resolveExternalId(faq);
  if (!externalId) return {...faq};
  return {...faq, externalId};
};

const ManageBlogFaqsContainer = ({
  contentId,
  faqsList,
  setFaqData,
  token,
  attachedFaqs,
  setAttachedFaqs
}) => {
  const [toggleAddFaqPopup, setToggleAddFaqPopup] = useState(false);
  const [toggleEditFaqPopup, setToggleEditFaqPopup] = useState(false);
  const [editFaq, setEditFaq] = useState({});

  const addIcon = `${WealthyEnv.HOMEPAGE_CDN_URL}/invest/plus-icon.png`;
  const editIcon = `${WealthyEnv.CDN_URL}/insurance/form-edit-icon.png`;
  const deleteIcon = `${WealthyEnv.CDN_URL}/articles/trash.png`;
  const arrowIcon = `${WealthyEnv.HOMEPAGE_CDN_URL}/partners-dropdown-arrow.svg`;

  const getFaqId = (faq) => faq?.externalId ?? null;

  const persistAttachedFaqs = async (nextList, listBeforeRequest) => {
    if (!contentId || !token) return;

    const faqItems = [];
    (nextList || []).forEach((item) => {
      const faqId = getFaqId(normalizeFaqForEditor(item));
      if (!faqId) return;
      faqItems.push({faq_id: faqId, order: faqItems.length + 1});
    });
    const faqDetails = faqItems;

    const faqsPayload = {
      "content_id": contentId,
      "faq_details": faqDetails
    };

    try {
      await updateFaq(token, JSON.stringify(faqsPayload));
    } catch (err) {
      if (typeof listBeforeRequest !== "undefined") {
        setAttachedFaqs(listBeforeRequest);
      }
      triggerToast("Failed to save FAQ changes", "error");
    }
  };

  const attachNewFaq = (rawFaq) => {
    const newItem = normalizeFaqForEditor(rawFaq);
    if (!newItem) return;

    const newItemId = getFaqId(newItem);
    if (!newItemId) {
      triggerToast("This FAQ cannot be attached (missing id).", "error");
      return;
    }
    const alreadyAttached = (attachedFaqs || []).some(
      (faq) => getFaqId(normalizeFaqForEditor(faq)) === newItemId
    );
    if (alreadyAttached) {
      triggerToast("FAQ is already attached", "info");
      return;
    }

    const previousList = [...(attachedFaqs || [])];
    const nextList = [...previousList, newItem];
    setAttachedFaqs(nextList);
    persistAttachedFaqs(nextList, previousList);
  };

  const attachFaq = (e, index) => {
    e.preventDefault();
    attachNewFaq(faqsList?.[index]);
  };

  const removeFaq = (e, index) => {
    e.preventDefault();
    const previousList = [...(attachedFaqs || [])];
    const nextList = [...previousList];
    nextList.splice(index, 1);
    setAttachedFaqs(nextList);
    persistAttachedFaqs(nextList, previousList);
  };

  const modifyFaq = (e, index) => {
    e.preventDefault();
    const raw = faqsList?.[index];
    const normalized = normalizeFaqForEditor(raw);
    if (!normalized || !getFaqId(normalized)) {
      triggerToast("Cannot edit this FAQ (missing id).", "error");
      return;
    }
    setToggleEditFaqPopup(true);
    setEditFaq(normalized);
  };

  const moveFaq = (e, index, moveTo) => {
    e.preventDefault();
    const previousList = [...(attachedFaqs || [])];
    const nextList = [...previousList];
    const temp = nextList[index];
    if (moveTo === "down") {
      if (index !== nextList.length - 1) {
        nextList[index] = nextList[index + 1];
        nextList[index + 1] = temp;
      } else {
        return;
      }
    } else {
      if (index !== 0) {
        nextList[index] = nextList[index - 1];
        nextList[index - 1] = temp;
      } else {
        return;
      }
    }
    setAttachedFaqs(nextList);
    persistAttachedFaqs(nextList, previousList);
  };

  const normalizedAttachedFaqs = attachedFaqs?.map((faq) =>
    normalizeFaqForEditor(faq)
  );

  const listAttachedFaqs = (normalizedAttachedFaqs?.map((item, index) => (
    <ListedFaqItem key={getFaqId(item) || `attached-${index}`}>
      <ItemTitle>
        <MoveControlsContainer>
          <MoveControls>
            <button type="button" onClick={(e) => moveFaq(e, index, "up")}>
              <img
                alt="up"
                className="arrow-up"
                src={arrowIcon}
              />
            </button>
            <button type="button" onClick={(e) => moveFaq(e, index, "down")}>
              <img
                alt="down"
                src={arrowIcon}
              />
            </button>
          </MoveControls>
          <h3>
            {item.question ?? ""}
          </h3>
        </MoveControlsContainer>
        <button type="button" onClick={(e) => removeFaq(e, index)}>
          <img
            alt="delete"
            src={deleteIcon}
          />
        </button>
      </ItemTitle>
      <p>
        {item.answer ?? ""}
      </p>
    </ListedFaqItem>
  )));

  const listFaqs = (faqsList?.map((item, index) => (
    <ListedFaqItem key={getFaqId(normalizeFaqForEditor(item)) || `db-${index}`}>
      <ItemTitle>
        <h3>
          {item.question ?? ""}
        </h3>
        <div>
          <button
            type="button"
            onClick={(e) => modifyFaq(e, index)}
          >
            <img
              alt="edit"
              src={editIcon}
            />
          </button>
          <button type="button" onClick={(e) => attachFaq(e, index)}>
            <img
              alt="add"
              src={addIcon}
            />
          </button>
        </div>
      </ItemTitle>
      <p>
        {item.answer ?? ""}
      </p>
    </ListedFaqItem>
  )));

  return (
    <ManageBlogFaqs
      attachNewFaq={attachNewFaq}
      contentId={contentId}
      editFaq={editFaq}
      faqsList={faqsList}
      listAttachedFaqs={listAttachedFaqs}
      listFaqs={listFaqs}
      setFaqData={setFaqData}
      setToggleAddFaqPopup={setToggleAddFaqPopup}
      setToggleEditFaqPopup={setToggleEditFaqPopup}
      toggleAddFaqPopup={toggleAddFaqPopup}
      toggleEditFaqPopup={toggleEditFaqPopup}
      token={token}
    />
  );
};

ManageBlogFaqsContainer.propTypes = {
  attachedFaqs: PropTypes.arrayOf(PropTypes.object),
  contentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  faqsList: PropTypes.arrayOf(PropTypes.object),
  setAttachedFaqs: PropTypes.func.isRequired,
  setFaqData: PropTypes.func.isRequired,
  token: PropTypes.string
};

ManageBlogFaqsContainer.defaultProps = {
  attachedFaqs: [],
  contentId: null,
  faqsList: [],
  token: ""
};

const ListedFaqItem = styled.li`
  list-style: none;
  margin: 1rem;
  padding: 1rem 1rem;
  border-radius: 0.3rem;
  background-color: #fff;
  box-shadow: 0 0 2px #6725f42e;

  &:not(:last-child) {
    margin-bottom: 1.5rem;
  }

  h3, p {
    margin: 0;
  }
  p {
    border-top: 1px solid #efefef;
    padding-top: 1rem;
    margin-top: 1rem;
  }
  button {
    background-color: transparent;
    border: 0;
    cursor: pointer;
    height: 30px;
    width: 30px;
    border-radius: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    &:not(:last-child) {
      margin-right: 0.4rem;
    }
    img {
      height: 22px;
      &.arrow-up{
        transform: rotate(180deg);
      }
    }
    &:hover {
      background-color: #f9f6ff;
    }
  }
`;

const ItemTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MoveControls = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 0.5rem;

  button {
    height: 15px;
    width: 15px;

    img {
      height: 100%;
    }
  }
`;

const MoveControlsContainer = styled.div`
  display: flex;
  align-items: center;
`;

export default ManageBlogFaqsContainer;
